'use client';
import {Card, CardContent} from "@/shadcn/components/ui/card";
import {Variant} from "@/types";
import {Badge} from "@/shadcn/components/ui/badge";
import {Package, Truck} from "lucide-react";
import {Tooltip, TooltipContent, TooltipProvider, TooltipTrigger} from "@/shadcn/components/ui/tooltip";
import {AspectRatio} from "@/shadcn/components/ui/aspect-ratio";
import {LandingRating} from "@/../components/landing/rating/LandingRating";
import {hasDisplayableRating} from "@/lib/ratings-visibility";
import VariantImage from "@/components/variants/variant-image";
import {FavoriteButton} from "@/components/favorites/favorite-button";
import {VariantCardOffersLink} from "@/components/variants/variant-card-offers-link";
import {AffiliateCardWrapper} from "@/components/affiliate-card-wrapper";
import {addToSeenProducts} from "@/rest/localstorage";
import {usePathname} from "next/navigation";
import {trackProductViewAsync} from "@/lib/analytics-wrapper";
import {trackProductClick} from "@/components/cookie-consent/utils.tracking";
import {formatPrice} from "@/lib/format-utils";
import {getProductUrl} from "@/lib/product-url";


const ResponsiveVariantCard = ({variant, priority = false}: { variant: Variant; priority?: boolean }) => {
    const pathname = usePathname();
    if (!variant) return null;

    // Server-computed affiliate URL (may be empty). Product page URL used as
    // fallback when no affiliate tracking is available so the card remains clickable.
    const affiliateUrl = variant.trackingUrl || undefined;
    const productUrl = getProductUrl(variant);

    // Calculate discount percentage if the product is on sale
    const isOnSale = variant?.discount > 0 && variant?.price_old > 0;
    const isFreeShipping = variant?.delivery_cost == 0;
    const discountPercentage = isOnSale
        ? Math.round(variant.discount)
        : null;


    // Inside ProductCard component
    const handleProductClick = async () => {
        addToSeenProducts(variant);  // Assuming 'product' is the prop with product details

        // Run analytics tracking asynchronously in background
        trackProductViewAsync(variant);

        // Add GTM tracking
        trackProductClick({
            // item_id = EAN for consistency with view_item + generate_lead.
            item_id: variant.ean,
            item_name: variant.title,
            item_category: variant.category_identifier,
            item_brand: variant.brand_name,
            price: variant.price,
            currency: 'EUR',
            source_page: pathname,
            source_url: window.location.href
        });
    };


    return (<TooltipProvider delayDuration={100}>
            <Tooltip>
                {/* Card is wrapped in relative container for button positioning */}
                <div className="relative h-full">
                    <AffiliateCardWrapper
                        trackingUrl={affiliateUrl}
                        fallbackHref={productUrl}
                        onBeforeOpen={handleProductClick}
                        ariaLabel={variant.title}
                        shopName={variant.best_cpc_shop_name || variant.cheapest_shop_name}
                        variantId={variant.ean}
                        variantTitle={variant.title}
                        variantPrice={variant.price}
                        itemCategory={variant.category_identifier}
                        itemBrand={variant.brand_name}
                        className="block h-full cursor-pointer"
                    >
                        <Card className="max-w-full overflow-hidden h-full w-full">
                            <CardContent className="flex items-center justify-center p-0 relative w-full h-full">

                                <div className="w-full h-full mx-auto bg-white rounded-lg overflow-hidden duration-300 relative">
                                    <div className={'relative w-full h-0 pt-[77%] sm:pt-[75%]'}>
                                        <div className={'absolute top-0 left-0 h-full w-full overflow-hidden'}>
                                            <AspectRatio ratio={4 / 3} className="bg-muted pt-1">
                                                <VariantImage
                                                    src={variant?.images?.[0] || '/img/furniture-sketch.svg'}
                                                    alt={variant?.title ?? ''}
                                                    width={400}
                                                    height={300}
                                                    priority={priority}
                                                    blurDataURL={variant.image_blur}
                                                    sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 20vw"
                                                    className="pointer-events-none w-full h-full mx-auto object-contain"
                                                />
                                            </AspectRatio>

                                        </div>
                                        <div className={'absolute inset-0  w-full h-full pointer-events-none'}>
                                            <div className={'absolute h-full flex flex-col gap-1 top-1 right-1 pointer-events-auto'}>
                                                <FavoriteButton variant={variant} size={'sm'}/>
                                            </div>

                                            <div className={`absolute h-full flex flex-col gap-1  right-1 items-end justify-between top-10`}>
                                                {(isOnSale && discountPercentage) ? (
                                                    <Badge className="bg-rose-600 text-white  right:0.5 md:right-1 hover:bg-rose-600  md:text-xs text-[0.75rem]">
                                                        {`-${discountPercentage}% `}
                                                    </Badge>
                                                ) : <>
                                                    <div></div>
                                                </>}
                                            </div>


                                        </div>
                                    </div>


                                    <div className="p-2 sm:p-4 absolute bottom-0 left-0 right-0 w-full">
                                        {hasDisplayableRating(variant) && (
                                            <div className="absolute top-0 flex items-center mt-3  text-yellow-400">
                                                <LandingRating size="xs" className="fill-yellow-400" rating={variant?.average_rating ?? 0}/>
                                                <span className="ml-2 text-gray-500 font-semibold text-[0.65rem] ">
                                                    {variant.average_rating ? variant?.average_rating.toFixed(1) : 0}
                                                </span>
                                            </div>
                                        )}
                                        <div className="flex flex-col justify-center items-start mt-5 sm:mt-4 mb-2">
                                            {isOnSale ? (
                                                <div className={'flex justify-start items-center relative'}>
                                                    <span className="text-rose-600 font-semibold text-sm sm:text-base flex flex-nowrap">
                                                        <span className={'font-light text-gray-700 mr-1'}>ab</span> {formatPrice(variant?.price)}
                                                    </span>
                                                    <span className="hidden md:flex ml-2 text-gray-500 font-semibold text-[0.65rem] line-through absolute top-0.5 -right-14">
                                                        {formatPrice(variant?.price_old)}
                                                    </span>
                                                </div>
                                            ) : (
                                                <span className="text-gray-900 font-semibold text-sm sm:text-base">
                                                    <span className={'font-light'}>ab</span> {formatPrice(variant?.price)}
                                                </span>
                                            )}

                                            <span className="text-gray-500 text-[0.65rem] xs:text-xs flex gap-1 mt-1 items-center h-3">
                                                {isFreeShipping ? null : <><Package className={'h-3 w-3'}/> {formatPrice(variant?.delivery_cost)}</>}
                                                {isFreeShipping && (
                                                    <Badge className="flex gap-1 bg-emerald-100 text-emerald-900 text-[0.7rem]  hover:bg-emerald-100 rounded ">
                                                        <Truck className="h-4 w-4 "/>
                                                        <span>
                                                            Gratis
                                                        </span>
                                                        <span className={'hidden sm:inline'}>
                                                            Lieferung
                                                        </span>
                                                    </Badge>
                                                )}
                                            </span>
                                        </div>


                                        <TooltipTrigger asChild>
                                            <h3 className="hidden scroll-m-20 text-[0.7rem] xs:text-xs sm:text-sm font-normal tracking-tight truncate  md:flex">
                                                {variant.title}
                                            </h3>
                                        </TooltipTrigger>
                                        {/* Placeholder for button positioning */}
                                        <div className={'flex w-full mt-2 gap-1 items-center justify-start h-7 md:h-8'}/>

                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </AffiliateCardWrapper>
                    {/* Buttons positioned absolutely outside the card wrapper to prevent event conflicts */}
                    <div className="absolute bottom-2 sm:bottom-4 left-2 sm:left-4 right-2 sm:right-4 z-10">
                        <VariantCardOffersLink variant={variant}/>
                    </div>
                </div>
                <TooltipContent side="bottom" className="max-w-72 bg-gray-100 text-gray-700" key={variant.variant_id}>
                    <p className={'text-xs font-light'}>{variant.title}</p>
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );
};

export default ResponsiveVariantCard;
